import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import useSignupStore from "../../../store/SignUpStore";
import {personalInfoSchema, validatePersonalInfo} from "../../../utils/personalInfoSchema";
import {
    useSignupMutation,
    useVerifyPhoneMutation,
} from "../../../api/SignUpAPI";

const PersonalInfoPage = () => {
    const {
        setPersonalInfo,
        marketingOptIn,
        loginId,
        password,
        resetForm,
        setLoading,
        setError,
        error,
        isLoading,
        ...otherSignupData
    } = useSignupStore();
    const navigate = useNavigate();
    const [verificationStatus, setVerificationStatus] = useState("idle");
    const [verificationMessage, setVerificationMessage] = useState("");
    const [remainingTime, setRemainingTime] = useState(180); // 3 minutes in seconds
    const [showCouponPopup, setShowCouponPopup] = useState(false);

    const signupMutation = useSignupMutation();
    const verifyPhoneMutation = useVerifyPhoneMutation();

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        resolver: zodResolver(personalInfoSchema),
        mode: "onChange",
    });

    const phoneNumber = watch("phoneNumber");
    const authNumber = watch("authNumber");

    useEffect(() => {
        let timer;
        if (verificationStatus === "sent" && remainingTime > 0) {
            timer = setInterval(() => {
                setRemainingTime((prevTime) => prevTime - 1);
            }, 1000);
        } else if (remainingTime === 0) {
            setVerificationStatus("idle");
        }
        return () => clearInterval(timer);
    }, [verificationStatus, remainingTime]);
    
    const handlePhoneVerification = async () => {
        setVerificationStatus("sending");
        setVerificationMessage("");
        setError(null);

        try {
            const response = await verifyPhoneMutation.mutateAsync({ phoneNumber });
            setVerificationStatus("sent");
            setVerificationMessage(
                "인증번호가 발송되었습니다. 문자메시지를 확인해주세요."
            );
            setRemainingTime(180); // 타이머 3분 설정
        } catch (error) {
            setVerificationMessage("전화번호 인증 요청에 실패했습니다.");
            // setVerificationMessage(error.message);
            setVerificationStatus("idle");
        }
    };

    const onSubmit = async (data) => {
        console.log("Form submitted with data:", data);  // 디버깅을 위한 로그

        const validationResult = validatePersonalInfo(data);
        if (!validationResult.success) {
            console.log("Validation errors:", validationResult.errors);  // 디버깅을 위한 로그
            setError("입력한 정보를 다시 확인해주세요.");
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const signupData = {
                ...otherSignupData,
                ...data,
                loginId,
                password,
                authNumber: data.authNumber,
                marketingOptIn,
                timeStamp: Date.now(),
            };

            console.log("Sending signup data:", signupData);  // 디버깅을 위한 로그

            // 수정 : 백엔드에서 인증번호 확인 결과를 포함한 응답을 처리
            const response = await signupMutation.mutateAsync(signupData);

            // error handling: 백엔드에서 인증번호 확인 실패 시 예외 처리
            if (response.error) {
                setError(response.error);
                setVerificationMessage(error.message || "회원가입에 실패했습니다. 다시 시도해 주세요.");
                return;
            }
            
            // 회원가입 성공 시 쿠폰 발급 알림 팝업 표시
            setShowCouponPopup(true);
            
            // 3초 후 메인 페이지로 이동
            setTimeout(() => {
                resetForm();
                navigate("/");
            }, 3000);

        } catch (error) {
            alert("회원가입에 실패했습니다. 다시 시도해주세요.");
            setError(
                error.message || "회원가입에 실패했습니다. 다시 시도해 주세요."
            );
            setVerificationMessage("회원가입에 실패했습니다. 다시 시도해 주세요.");
        } finally {
            setLoading(false);
        }
    };

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${
            remainingSeconds < 10 ? "0" : ""
        }${remainingSeconds}`;
    };

    return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-white px-4 mt-[140px]">
            <div className="w-full max-w-md space-y-8">
                <div className="text-center">
                    <h1 className="text-2xl font-bold">회원가입</h1>
                    <p className="mt-2 text-gray-600">
                        개인정보를 입력해주세요
                    </p>
                </div>
                {error && <p className="text-red-500 mt-2">{error}</p>}
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="mt-8 space-y-4"
                >
                    <div>
                        <input
                            {...register("name")}
                            type="text"
                            placeholder="이름"
                            className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.name && (
                            <p className="mt-1 text-sm text-red-500">
                                {errors.name.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <input
                            {...register("birth")}
                            type="text"
                            placeholder="생년월일 8자리 (YYYYMMDD)"
                            className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.birth && (
                            <p className="mt-1 text-sm text-red-500">
                                {errors.birth.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <input
                            {...register("email")}
                            type="email"
                            placeholder="이메일"
                            className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {errors.email && (
                            <p className="mt-1 text-sm text-red-500">
                                {errors.email.message}
                            </p>
                        )}
                    </div>

                    <div className="flex gap-2">
                        <div className="flex-1">
                            <input
                                {...register("phoneNumber")}
                                type="tel"
                                placeholder="전화번호 ('-' 없이 숫자만 입력)"
                                className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.phoneNumber && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.phoneNumber.message}
                                </p>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={handlePhoneVerification}
                            disabled={
                                verificationStatus === "sending" || !phoneNumber
                            }
                            className="px-4 h-[60px] py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 whitespace-nowrap"
                        >
                            {verificationStatus === "idle" ||
                            remainingTime === 0
                                ? "인증요청"
                                : verificationStatus === "sending"
                                ? "요청중..."
                                : "재요청"}
                        </button>
                    </div>
                    {verificationMessage && (
                        <p
                            className={`mt-2 text-sm ${
                                verificationMessage.includes("실패") || verificationMessage.includes("이미 가입된 회원입니다.") || verificationMessage.includes("Unexpected")
                                    ? "text-red-500"
                                    : "text-green-500"
                            }`}
                        >
                            {verificationMessage}
                        </p>
                    )}
                    {verificationStatus === "sent" && remainingTime > 0 && (
                        <p className="mt-1 text-sm text-gray-500">
                            남은 시간: {formatTime(remainingTime)}
                        </p>
                    )}
                    {verificationStatus === "sent" && (
                        <div>
                            <input
                                {...register("authNumber")}
                                type="text"
                                placeholder="인증번호 입력"
                                className="w-full h-[60px] px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            {errors.authNumber && (
                                <p className="mt-1 text-sm text-red-500">
                                    {errors.authNumber.message}
                                </p>
                            )}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isLoading || !authNumber || verificationStatus !== "sent"}
                        className="w-full h-[60px] py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 mt-6 disabled:opacity-50"
                    >
                        {isLoading ? "처리 중..." : "회원가입"}
                    </button>
                </form>
            </div>

            <div className="flex justify-center mt-[150px]">
                <img
                    src="/images/softcat_new_logo.png"
                    alt=" "
                    className="w-[180px] h-[50px]"
                />
            </div>
            
            {/* 쿠폰 발급 알림 팝업 */}
            {showCouponPopup && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
                        <div className="text-center">
                            <div className="text-5xl mb-4">🎉</div>
                            <h2 className="text-2xl font-bold mb-4">회원가입을 축하합니다!</h2>
                            <p className="text-lg mb-6">
                                가입 축하 쿠폰이 발급되었습니다.<br />
                                마이페이지에서 확인하실 수 있습니다.
                            </p>
                            <p className="text-sm text-gray-500">3초 후 자동으로 메인 페이지로 이동합니다.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PersonalInfoPage;
